import type {
  GetUsersParamsType,
  ListProjectsMinimalParams,
  ListTeamsParams,
  TeamTypeEnum,
} from "@nlc/shared-types";
import type { QueryClient } from "@tanstack/react-query";

export const brandsKeys = {
  all: ["brands"] as const,
  brandsByProjectId: (projectId?: number) =>
    [...brandsKeys.all, projectId] as const,
  brandsList: (searchParams: {
    direction: "asc" | "desc";
    globalMode?: boolean;
    isSuperAdmin?: boolean;
    itemsPerPage: number;
    page: number;
    projectId?: number;
    search?: string;
    sortBy: string;
  }) => [...brandsKeys.all, "list", searchParams] as const,
};

export const checkBrandNameKeys = {
  all: ["checkBrandName"] as const,
  check: (projectId: number, name: string) =>
    [...checkBrandNameKeys.all, projectId, name] as const,
};

export const brandRequestsKeys = {
  all: ["brand-requests"] as const,
  list: (brandId: number, params: Record<string, unknown>) =>
    [...brandRequestsKeys.all, brandId, "list", params] as const,
};

export const assignableBrandsByRequestId = {
  all: ["assignableBrandsByRequestId"] as const,
  brandsByRequestId: (requestId?: number) =>
    [...assignableBrandsByRequestId.all, requestId] as const,
};

export const getBrandById = {
  all: ["getBrandById"] as const,
  getBrandById: (brandId?: null | number) =>
    [...getBrandById.all, brandId] as const,
};

export const brandNotesKeys = {
  all: ["brand-notes"] as const,
  byBrandId: (brandId: number) => [...brandNotesKeys.all, brandId] as const,
};

export const visualDirectionKeys = {
  all: ["visual-direction"] as const,
  byBrandId: (brandId: number) =>
    [...visualDirectionKeys.all, brandId] as const,
};

export const preferredDesignersKeys = {
  all: ["preferred-designers"] as const,
  preferredDesignersByProjectId: (projectId?: number) =>
    [...preferredDesignersKeys.all, projectId] as const,
};

export const collaboratorsKeys = {
  all: ["collaborators"] as const,
  collaboratorsByProjectId: (projectId?: number) =>
    [...collaboratorsKeys.all, projectId] as const,
};

export const designTypesKeys = {
  all: ["design-types"] as const,
  designTypesByProjectId: (projectId?: number) =>
    [...designTypesKeys.all, projectId] as const,
  // Unique key for fetching design types by request id (used in edit request flow)
  designTypesByRequestId: (requestId?: number) =>
    [...designTypesKeys.all, "by-request", requestId] as const,
  picker: (projectId?: number) =>
    [...designTypesKeys.all, "picker", projectId] as const,
};

export const requestListKeys = {
  all: ["request-list"] as const,
  requestListByProjectId: (projectId: number, category?: string[]) =>
    [...requestListKeys.all, projectId, category] as const,
};

export const projectRequestsKeys = {
  all: ["project-requests"] as const,
} as const;

export const projectRequestStatisticsKeys = {
  all: ["project-requests", "statistics"] as const,
  byProjectId: (projectId: number) =>
    [...projectRequestStatisticsKeys.all, projectId] as const,
};

export const userByIdKeys = {
  all: ["user-by-id"] as const,
  userById: (userId: string) => [...userByIdKeys.all, userId] as const,
};

export const updateRolesKeys = {
  all: ["user-update-roles"] as const,
  byTargetUserId: (targetUserId?: string) =>
    [...updateRolesKeys.all, targetUserId] as const,
};

export const projectsByOwnerKeys = {
  all: ["projects-by-owner"] as const,
  byUserId: (userId: string) => [...projectsByOwnerKeys.all, userId] as const,
};

export const usersKeys = {
  all: ["users"] as const,
  archive: (userId: number | undefined) =>
    [...userByIdKeys.all, userId] as const,
  delete: (userId: number | undefined) =>
    [...userByIdKeys.all, userId] as const,
  unarchive: (userId: number | undefined) =>
    [...userByIdKeys.all, userId] as const,
  usersBySearchParams: (searchParams: GetUsersParamsType) =>
    [...usersKeys.all, searchParams] as const,
  usersList: (searchParams: {
    category?: string;
    direction: "asc" | "desc";
    globalMode?: boolean;
    itemsPerPage: number;
    page: number;
    projectId?: number;
    search?: string;
    sortBy: string;
  }) => [...usersKeys.all, "list", searchParams] as const,
};

export const feedbackToolKeys = {
  all: ["feedback-tool"] as const,
  createFeedback: (parentId?: number) =>
    [...feedbackToolKeys.all, parentId] as const,
  delete: (commentBatchId?: string) =>
    [...feedbackToolKeys.all, commentBatchId] as const,
  deleteAttachment: (parentId?: number) =>
    [...feedbackToolKeys.all, parentId] as const,
  feedbacks: (parentId?: number) =>
    [...feedbackToolKeys.all, parentId] as const,
  resolve: (commentBatchId?: string) =>
    [...feedbackToolKeys.all, commentBatchId] as const,
  update: (storyId?: number) => [...feedbackToolKeys.all, storyId] as const,
};

export const requestKeys = {
  all: ["request"] as const,
  completionMode: (requestId: number) =>
    [...requestKeys.all, requestId, "completion-mode"] as const,
  rating: (requestId: number) =>
    [...requestKeys.all, requestId, "rating"] as const,
  ratingSummary: () => [...requestKeys.all, "ratings", "summary"] as const,
  requestById: (requestId: number) => [...requestKeys.all, requestId] as const,
};

export const creatorOptionsKeys = {
  all: ["creator-options"] as const,
  byRequestId: (requestId?: number) =>
    [...creatorOptionsKeys.all, requestId] as const,
};

export const uploadingSessionKeys = {
  active: (requestId: number) =>
    [...uploadingSessionKeys.all, "active", requestId] as const,
  all: ["uploadingSession"] as const,
  policy: (requestId: number) =>
    [...uploadingSessionKeys.all, "policy", requestId] as const,
  snapshot: (sessionId: null | string) =>
    [...uploadingSessionKeys.all, "snapshot", sessionId] as const,
};

export const attachmentsKeys = {
  all: ["attachments"] as const,
  attachmentsByRequestId: (requestId: number) =>
    [...attachmentsKeys.all, requestId] as const,
  createConversion: (attachmentId: number) =>
    [...feedbackToolKeys.all, "create-conversion", attachmentId] as const,
  getConversions: (attachmentId: number) =>
    [...feedbackToolKeys.all, "get-conversion", attachmentId] as const,
};
export const assignableDesignersKeys = {
  all: ["assignable-designers"] as const,
  assignableDesignersByRequestId: (requestId?: number) =>
    [...assignableDesignersKeys.all, requestId] as const,
};

export const linkableRequestsKeys = {
  all: ["linkable-requests"] as const,
  linkableRequestsByRequestId: (requestId?: number) =>
    [...linkableRequestsKeys.all, requestId] as const,
};

export const projectsKeys = {
  all: ["projects"] as const,
  designerPerformanceSummary: (
    designerId: number,
    filters?: Record<string, unknown>,
  ) =>
    [
      ...projectsKeys.all,
      "designer-performance-summary",
      designerId,
      filters,
    ] as const,
  viewSummary: (projectId: string, filters?: Record<string, unknown>) =>
    [...projectsKeys.all, "view-summary", projectId, filters] as const,
};

export const projectUsersKeys = {
  all: ["project-users"] as const,
};

export const projectInformationKeys = {
  all: ["project-information"] as const,
  editPage: (projectId?: number) =>
    [...projectInformationKeys.all, projectId] as const,
};

export const notificationsKeys = {
  all: ["notifications"] as const,
  preferences: () => [...notificationsKeys.all, "preferences"] as const,
  settings: () => [...notificationsKeys.all, "settings"] as const,
  updateCustomProperties: () =>
    [...notificationsKeys.all, "custom-properties"] as const,
  updatePreferences: () =>
    [...notificationsKeys.all, "update-preferences"] as const,
  updateSlack: (code: string | undefined) =>
    [...notificationsKeys.all, code ?? "slack"] as const,
  user: (email: string) => [...notificationsKeys.all, email] as const,
};

export const appNotificationsKeys = {
  all: ["app-notifications"] as const,
  list: (params: { isArchived: boolean; itemsPerPage: number; tag?: string }) =>
    [...appNotificationsKeys.all, "list", params] as const,
  unreadCounts: () => [...appNotificationsKeys.all, "unread-counts"] as const,
};

export const teamsKeys = {
  all: ["teams"] as const,
  availableUsers: (type: TeamTypeEnum, teamId?: number) =>
    [...teamsKeys.all, "available-users", { teamId, type }] as const,
  detail: (id: number | string) => [...teamsKeys.all, "detail", id] as const,
  list: (params: ListTeamsParams) =>
    [...teamsKeys.all, "list", params] as const,
};

// Role-specific query keys
export const designerKeys = {
  all: ["designerRequests"] as const,
  counts: ["designerRequestCounts"] as const,
};

export const teamLeadKeys = {
  all: ["teamLeadRequests"] as const,
  counts: ["teamLeadCounts"] as const,
  teamRequests: ["teamLeadTeamRequests"] as const,
  unassignedRequests: ["teamLeadUnassignedRequests"] as const,
};

export const customerSuccessKeys = {
  counts: ["customerSuccessCounts"] as const,
};

export const superAdminKeys = {
  counts: ["request-list", "superadmin-counts"] as const,
};

// Key groups for common invalidation patterns
export const allRequestListKeys = [
  requestListKeys.all,
  designerKeys.all,
  teamLeadKeys.all,
  teamLeadKeys.teamRequests,
  teamLeadKeys.unassignedRequests,
  projectRequestsKeys.all,
] as const;

export const allCountKeys = [
  designerKeys.counts,
  teamLeadKeys.counts,
  superAdminKeys.counts,
  customerSuccessKeys.counts,
] as const;

// Utility functions for batch query operations
export function invalidateQueryKeys(
  queryClient: QueryClient,
  keys: readonly (readonly unknown[])[],
): void {
  for (const queryKey of keys) {
    queryClient.invalidateQueries({ queryKey });
  }
}

export function refetchQueryKeys(
  queryClient: QueryClient,
  keys: readonly (readonly unknown[])[],
  options?: { type?: "active" | "all" },
): void {
  const type = options?.type ?? "active";

  for (const queryKey of keys) {
    queryClient.refetchQueries({ queryKey, type });
  }
}

export const trashKeys = {
  all: ["trash"] as const,
  brands: (params: {
    direction?: string;
    itemsPerPage?: number;
    page?: number;
    search?: string;
    sortBy?: string;
  }) => [...trashKeys.all, "brands", params] as const,
  projects: (params: {
    direction?: string;
    itemsPerPage?: number;
    page?: number;
    search?: string;
    sortBy?: string;
  }) => [...trashKeys.all, "projects", params] as const,
  requests: (params: {
    direction?: string;
    limit?: number;
    page?: number;
    search?: string;
    sortBy?: string;
  }) => [...trashKeys.all, "requests", params] as const,
  users: (params: {
    direction?: string;
    itemsPerPage?: number;
    page?: number;
    search?: string;
    sortBy?: string;
  }) => [...trashKeys.all, "users", params] as const,
};
export const availableFontsKeys = {
  all: ["available-fonts"] as const,
};

export const getProjectsQueryKey = (params?: ListProjectsMinimalParams) =>
  ["projects", "minimal", params] as const;

export const profileKeys = {
  all: ["profile"] as const,
  brandsByUserId: (userId: number | string) =>
    [...profileKeys.all, "brands", userId] as const,
  requestsByUserId: (userId: number | string) =>
    [...profileKeys.all, "requests", userId] as const,
  userById: (userId: number | string) =>
    [...profileKeys.all, "user", userId] as const,
};

export const whitelabelKeys = {
  all: ["whitelabel"] as const,
  current: ["whitelabel", "current"] as const,
  dashboard: () => [...whitelabelKeys.all, "dashboard"] as const,
  detail: (id: number | string) =>
    [...whitelabelKeys.all, "detail", id] as const,
  list: () => [...whitelabelKeys.all, "list"] as const,
  request: (requestId: number | undefined) =>
    [...whitelabelKeys.all, "request", requestId] as const,
};

export const projectInternalNotesKeys = {
  all: ["project-internal-notes"] as const,
  byProjectId: (projectId: number) =>
    [...projectInternalNotesKeys.all, projectId] as const,
};

export const requestInternalNotesKeys = {
  all: ["request-internal-notes"] as const,
  byRequestId: (requestId: number) =>
    [...requestInternalNotesKeys.all, requestId] as const,
};

export const projectDetailsKeys = {
  all: ["project-details"] as const,
  detail: (projectId: number) =>
    [...projectDetailsKeys.all, projectId] as const,
  options: (projectId: number) =>
    [...projectDetailsKeys.all, "options", projectId] as const,
};

export const confidenceCheckerAdminKeys = {
  all: ["confidence-checker-admin"] as const,
  settings: () => [...confidenceCheckerAdminKeys.all, "settings"] as const,
};

export const mbcAdminKeys = {
  all: ["mbc-admin"] as const,
  assignments: () => [...mbcAdminKeys.all, "assignments"] as const,
  catalog: () => [...mbcAdminKeys.all, "catalog"] as const,
  checks: () => [...mbcAdminKeys.all, "checks"] as const,
  guidelines: () => [...mbcAdminKeys.all, "guidelines"] as const,
};

export const socialPublishingKeys = {
  accounts: () => [...socialPublishingKeys.all, "accounts"] as const,
  all: ["social-publishing"] as const,
  audienceInsights: (filters: object) =>
    [...socialPublishingKeys.all, "audience-insights", filters] as const,
  connection: () => [...socialPublishingKeys.all, "connection"] as const,
  connectSession: () =>
    [...socialPublishingKeys.all, "connect-session"] as const,
  insightsOverview: (filters: object) =>
    [...socialPublishingKeys.all, "insights-overview", filters] as const,
  pages: () => [...socialPublishingKeys.all, "pages"] as const,
  publishedPost: (id: number) =>
    [...socialPublishingKeys.all, "published-post", id] as const,
  publishedPosts: (filters: object) =>
    [...socialPublishingKeys.all, "published-posts", filters] as const,
  scheduledPosts: () =>
    [...socialPublishingKeys.all, "scheduled-posts"] as const,
};

export const subscriptionKeys = {
  all: ["subscription"] as const,
  overview: (projectId: number) =>
    [...subscriptionKeys.all, "overview", projectId] as const,
};
